import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Grid, Typography, CircularProgress } from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { SearchBarComponent } from "../../../component/atom";
import HeadingComponent from "../../../component/atom/Headings/Heading";
import DropDownComponent from "../../../component/atom/Inputs/DropDown";
import DocumentBreadcrumbs from "../../../component/molecule/Document/BreadCrumb";
import DocumentGrid from "../../../component/molecule/Document/DocumentGrid";
import { Actions } from "../../../core/modules/Actions";
import { connect } from "react-redux";
import _ from "lodash";
import {
  getText,
  DOCUMENT_STATUS,
  getBaseUrl,
  resolveDocumentThumbnailSrc,
  syncSessionState,
  getStoredBreadcrumb,
} from "../../../core/Constant";
import { baseappURL } from "../../../core/repository/Repository";
import DocumentRepository from "../../../core/repository/DocumentRepository";
import { useFocusMode } from "../../../hook/useFocusMode";

const IS_DIRECTORY_FOLDER = 1;
const ROOT_BREADCRUMB = { id: null, name: "Smart Docs" };

const isFolder = (doc) => Number(doc?.is_directory) === IS_DIRECTORY_FOLDER;
const normalizeParentId = (parentId) => parentId == null || parentId === 0 ? null : Number(parentId);
const formatTitle = (name = "") => name.replace(/\.[^/.]+$/, "");

const DocumentMainScreen = ({
  getStdCourseList,
  stdCourseList,
  getDocumentList,
  documentList
}) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const getInitialState = () => {
    const savedFolderId = sessionStorage.getItem("smartdocs_currentFolderId");
    const savedStack = sessionStorage.getItem("smartdocs_folderStack") || getStoredBreadcrumb("document");

    if (savedFolderId && savedFolderId !== "null") {
      try {
        return {
          folderId: Number(savedFolderId),
          stack: typeof savedStack === "string" ? JSON.parse(savedStack) : savedStack || [],
        };
      } catch (e) {
        console.error("Error parsing session params", e);
      }
    }

    return { folderId: null, stack: [] };
  };

  const initialState = useMemo(() => getInitialState(), []);

  const [searchValue, setSearchValue] = useState("");
  const [newcourseList, setCourseList] = useState([]);
  const [course, setCourse] = useState("");
  const [baseUrl,setBaseUrl] = useState("");
  const [documents, setDocuments] = useState([]);
  const [recentDocuments, setRecentDocuments] = useState([]);
  const [currentFolderId, setCurrentFolderId] = useState(initialState.folderId);
  const [folderStack, setFolderStack] = useState(initialState.stack);
  const [folderLoading, setFolderLoading] = useState(false);

  const isInitialFetched = useRef(false);
  useFocusMode(folderLoading);

  const updateStateAndParams = (selectedCourse, folderId, stack) => {
    setCurrentFolderId(folderId);
    setFolderStack(stack);

    // Save Session Storage
    if (selectedCourse)
      sessionStorage.setItem("smartdocs_course", String(selectedCourse));

    if (folderId !== null) {
      sessionStorage.setItem("smartdocs_currentFolderId", String(folderId));
      sessionStorage.setItem("smartdocs_folderStack", JSON.stringify(stack));
    } else {
      sessionStorage.removeItem("smartdocs_currentFolderId");
      sessionStorage.removeItem("smartdocs_folderStack");
    }

    const params = new URLSearchParams();
    if (selectedCourse) params.set("course", String(selectedCourse));

    setSearchParams(params, { replace: true });
  };

  useEffect(() => {
    getStdCourseList();
  }, []);

  useEffect(() => {
    const list = _.map(stdCourseList, (item) => ({
        id: item.course_id,
        course_id: item.course_id,
        name: item.course_name,
      }
  ));
    setCourseList(list);

    if (!list.length) {
      setCourse("");
      setDocuments([]);
      setRecentDocuments([]);
      return;
    }

    const urlCourse = searchParams.get("course");
    const savedSessionCourse = sessionStorage.getItem("smartdocs_course") || urlCourse;

    const matchedCourse = list.find((c) => String(c.id) === String(savedSessionCourse))?.id;
    const validCourse = matchedCourse || list[0]?.id;

    if (validCourse) {
      setCourse(validCourse);
      sessionStorage.setItem("smartdocs_course", String(validCourse));

    if (!isInitialFetched.current) {
      isInitialFetched.current = true;
      fetchDocuments(currentFolderId, validCourse);
      }
    }
  }, [stdCourseList]);

  useEffect(() => {
    if (documentList?.documents) {
      const filteredDocuments = documentList.documents
        .filter((doc) => Number(doc.status) === DOCUMENT_STATUS.ACTIVE)
        .map((doc) => ({
          ...doc,
          resolvedThumbnail: resolveDocumentThumbnailSrc(doc.thumbnail, {
            isFolder: isFolder(doc),
            isImage: doc.mime_type?.startsWith("image/"),
          }),
        }));

      setDocuments(filteredDocuments);

    if (currentFolderId && filteredDocuments.length === 0) {
  const newStack = folderStack.slice(0, -1);
  const newParentId =
    newStack.length > 0
      ? newStack[newStack.length - 1].id
      : null;

        updateStateAndParams(course, newParentId, newStack);
        fetchDocuments(newParentId, course);
}
  }

    const resolvedBaseUrl =
      documentList?.base_url || getBaseUrl() || baseappURL;
    setBaseUrl(resolvedBaseUrl);
  }, [documentList]);

  useEffect(() => {
    const fetchRecentDocuments = async () => {
      if (!course) {
        setRecentDocuments([]);
        return;
      }
      const response = await DocumentRepository.getRecentViewedList(course);
      if (response?.error) {
        setRecentDocuments([]);
        return;
      }

      const listData = Array.isArray(response) ? response : [];
      const processedRecent = listData.map((doc) => ({
        ...doc,
        resolvedThumbnail: resolveDocumentThumbnailSrc(doc.thumbnail, {
          isFolder: isFolder(doc),
          isImage: doc.mime_type?.startsWith("image/"),
        }),
      }));

      setRecentDocuments(processedRecent);
    };

    fetchRecentDocuments();
  }, [course]);

  const courseName = useMemo(() => {
    const found = newcourseList.find((c) => String(c.id) === String(course));
    return found?.name || "";
  }, [newcourseList, course]);

  const resetFolderNavigation = () => {
    updateStateAndParams(course, null, []);
  };

  const fetchDocuments = (
    parentId = currentFolderId,
    selectedCourse = course,
    search = searchValue
  ) => {
    const activeCourse = selectedCourse || course;
    if (!activeCourse) return;

    const params = parentId != null && parentId !== 0      ? { course_id: activeCourse, parent_id: parentId }      : { course_id: activeCourse };
    if (search?.trim()) {
      params.search = search.trim();
    }

    getDocumentList(params);
  };

  const openFolder = async (doc) => {
  if (folderLoading) return;

  setFolderLoading(true);

  try {
      const nextFolderId = doc.id;
      let nextStack = [];

      const existingIndex = folderStack.findIndex(
        (folder) => folder.id === doc.id
      );
      if (existingIndex !== -1) {
        nextStack = folderStack.slice(0, existingIndex + 1);
      } else {
        nextStack = [
          ...folderStack,
          { id: doc.id, name: doc.name, status: doc.status },
        ];
      }

      updateStateAndParams(course, nextFolderId, nextStack);
      fetchDocuments(nextFolderId, course);
  } finally {
    setTimeout(() => {
      setFolderLoading(false);
    }, 500);
  }
};

  const navigateToBreadcrumb = (index) => {
    if (index === 0) {
      resetFolderNavigation();
      fetchDocuments(null, course);
      return;
    }

    const newStack = folderStack.slice(0, index);
    const folderId = newStack[newStack.length - 1]?.id ?? null;

    updateStateAndParams(course, folderId, newStack);
    fetchDocuments(folderId, course);
  };

  //Empty at root level
  const breadcrumbTrail = useMemo(() => {
    if (!folderStack || folderStack.length === 0) {
      return [];
    }

    if (courseName) {
      return [{ id: `course-${course}`, name: courseName }, ...folderStack];
    }

    return [ROOT_BREADCRUMB, ...folderStack];
  }, [courseName, course, folderStack]);

  const displayList = useMemo(() => {
    let items = documents;
    const hasParentIds = items.some(
      (d) => d.parent_id != null && d.parent_id !== "" && d.parent_id !== 0
    );
    if (hasParentIds) {
      const parentId = currentFolderId ?? null;
      items = items.filter((d) => {
        const docParentId = normalizeParentId(d.parent_id);
        if (parentId != null) {
          return docParentId === normalizeParentId(parentId);
        }
        return (
          docParentId === null || docParentId === normalizeParentId(course)
        );
      });
    }
    if (searchValue.trim()) {
      const q = searchValue.trim().toLowerCase();
      items = items.filter((d) => d.name?.toLowerCase().includes(q));
    }
    const folders = items.filter(isFolder);
    const files = items.filter((d) => !isFolder(d));
    return [...folders, ...files];
  }, [documents, currentFolderId, searchValue, course]);

  const onCourseChange = (e) => {
    const selectedValue = getText(e);
    setCourse(selectedValue);
    sessionStorage.setItem("smartdocs_course", String(selectedValue));
    setSearchValue("");
    updateStateAndParams(selectedValue, null, []);
    getDocumentList({ course_id: selectedValue });
  };

  const searchInput = (newvalue) => {
    setSearchValue(newvalue);
    fetchDocuments(currentFolderId, course, newvalue);
  };

  const navigateToDocumentScreen = (item) => {
    const routeState = {
        document: item,
        baseurl: baseUrl,
        returnPath: `/document${window.location.search}`,
        breadcrumbTrail,
        selectedCourse: course,
      };

      syncSessionState(routeState, "document");

      navigate("/view-document", { state: routeState });
  };

  const handleCardAction = (item) => {
    if (isFolder(item)) {
      openFolder(item);
      return;
    }
    navigateToDocumentScreen(item);
  };

  const getRelativeTime = (dateString) => {
  if (!dateString) return "";
  const dateStr = dateString.replace(/-/g, "/");
  const viewedAt = new Date(dateStr);
  const now = new Date();
  const diffInSeconds = Math.floor((now - viewedAt) / 1000);

  if (diffInSeconds < 60) return "Just now";
  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) return `${diffInMinutes} min${diffInMinutes > 1 ? "s" : ""} ago`;
  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) return `${diffInHours} hour${diffInHours > 1 ? "s" : ""} ago`;
  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 30) return `${diffInDays} day${diffInDays > 1 ? "s" : ""} ago`;

  return viewedAt.toLocaleDateString();
  };

  return (
      <Grid className="main-screen-container" container flexDirection={"column"} rowSpacing={1}  mt={2}>
        <Grid item>
          <Grid container justifyContent={"space-between"} alignItems={"flex-start"}>
            <Grid item>
              <HeadingComponent
                text={"Documents"}
                size={"26px"}
                color={"#2d3945"}
                fontfamily={"Montserrat"}
                fontweigth={600}
              />
            </Grid>
            <Grid item>
              <Grid container alignItems={"center"}>
                <Grid item sx={{ minWidth: "220px", width: { xs: "100%", lg: "350px" } }}>
                  <DropDownComponent
                    isShowPlaceholder={true}
                    isShowZero={false}
                    initialValue="Select Course"
                    onchange={onCourseChange}
                    radius={"15px"}
                    list={newcourseList}
                    selectedValue={course}
                    fixedWidth="100%"
                  />
                </Grid>
                <Grid item>
                  <SearchBarComponent
                    value={searchValue}
                    onchange={(e) => searchInput(e.target.value)}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      {/* Dynamic Refactored Breadcrumb Trail */}
      {breadcrumbTrail.length > 0 && (
        <Grid item>
        <DocumentBreadcrumbs breadcrumbTrail={breadcrumbTrail} onBreadcrumbClick={navigateToBreadcrumb} />
        </Grid>
      )}

      {/* Dynamic Refactored Recent Documents Grid */}
      {/* {recentDocuments.length > 0 && (
        <Grid item mb={6} mt={2}>
          <Typography
            sx={{
              fontSize: 18,
              fontWeight: 700,
              color: "#2d3945",
              fontFamily: "Montserrat",
              mb: 2,
            }}
          >
            Recently Viewed
          </Typography>
          <DocumentGrid
            items={recentDocuments}
            isFolder={isFolder}
            formatTitle={formatTitle}
            handleCardAction={handleCardAction}
            getRelativeTime={getRelativeTime}
            showTime={true}
            baseUrl={baseUrl}
          />
        </Grid>
      )} */}

      {/* Dynamic Refactored Main Document Grid View */}
      <Grid item>
        <DocumentGrid
          items={displayList}
          isFolder={isFolder}
          formatTitle={formatTitle}
          handleCardAction={handleCardAction}
          baseUrl={baseUrl}
        />
        </Grid>
        {folderLoading && (
                <Box
                  sx={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    bgcolor: "rgba(255,255,255,0.4)",
                    zIndex: 9999,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <CircularProgress />
                </Box>
              )}
      </Grid>
  );
};

export default connect(
  (state) => ({
    stdCourseList: state.course.get("stdCourseList"),
    documentList: state.document.get("documentList"),
  }),
  {
    getStdCourseList: Actions.course.getStdCourseList,
    getDocumentList:Actions.document.getDocumentList
  }
)(DocumentMainScreen);
